import {
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
  MutableRefObject,
} from 'react';
import { resizeListen, resizeUnlisten } from 'dom-resize';
import { throttle } from '@hox/frontend-utils/ramda';
import { useIsMounted } from '@hox/frontend-utils/hooks/useIsMounted';

// useTrackWindowResize disabled temporarily
// import { useTrackWindowResize } from './useTrackWindowResize';

// eslint-disable-next-line no-console
console.warn(
  'Using experimental `dom-resize` in `useLayoutMode`, notify @thatsprettyfaroutman if you experience glitcines when resizing'
);

export type TLayoutMode = 'small' | 'medium' | 'large';

const getMode = (width: number): TLayoutMode => {
  if (width <= 420) {
    return 'small';
  }
  if (width <= 600) {
    return 'medium';
  }
  return 'large';
};

export const useLayoutMode = (
  ref?: MutableRefObject<HTMLElement | null>,
  deps: any[] = [] // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
  const [mode, setMode] = useState<TLayoutMode>('small');
  const isMounted = useIsMounted();

  const handleResize = useCallback(
    throttle(() => {
      if (!ref || !ref.current || !isMounted.current) {
        return;
      }
      setMode(getMode(ref.current.clientWidth));
    }, 120),
    [ref]
  );

  useLayoutEffect(() => {
    if (!ref || !ref.current) {
      return;
    }
    const el = ref.current;
    handleResize();
    resizeListen(el, handleResize);
    return () => {
      try {
        resizeUnlisten(el, handleResize);
      } catch (error) {
        // 👉😎👉
        // dom-resize doesn't play well with react Portal
        // and the `el` is removed before resizeUnlisten is called
        // causing it to throw.
        // Shouldn't be a big problem - maybe something to keep an eye on
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, handleResize, ...deps]);

  // useTrackWindowResize disabled temporarily
  // useTrackWindowResize({ onResize: handleResize }, [ref, ...deps]);

  return mode;
};

export const useLayoutModeRef = () => {
  const ref = useRef<HTMLElement | null>(null);
  const layoutMode = useLayoutMode(ref);

  return {
    ref,
    layoutMode,
  };
};
